import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { twJoin, twMerge } from "tailwind-merge";

import { Icon } from "src/design-system/Icon";
import { Tooltip, TooltipProps } from "src/design-system/Tooltip";

type CommonProps = {
  className?: string;
  description?: React.ReactNode;
  helpTooltip?: TooltipProps["title"];
  helpTooltipBody?: TooltipProps["body"];
  helpAction?: TooltipProps["footerAction"];
  helpClassName?: string;
  isRequired?: boolean;
  dataLoc?: string;
};

type Props = {
  label?: React.ReactNode;
  children?: React.ReactNode;
  labelClassName?: string;
  gap?: "xxs" | "xs" | "sm" | "base" | "lg" | false;
  id?: string;
} & CommonProps;

type LabelProps = {
  children: React.ReactNode;
} & CommonProps;

type InfoProps = {
  title?: TooltipProps["title"];
  body?: TooltipProps["body"];
  action?: TooltipProps["footerAction"];
  className?: string;
};

type SubComponents = {
  Label: React.FC<LabelProps>;
  Help: React.FC<InfoProps>;
};

export const FormItem: React.FC<Props> & SubComponents = ({
  children,
  label,
  className,
  labelClassName,
  helpClassName,
  gap = "base",
  id,
  dataLoc,
  ...props
}) => (
  <div
    className={twMerge(
      "last:mb-0",
      gap === false && "mb-0",
      gap === "xxs" && "mb-2",
      gap === "xs" && "mb-4",
      gap === "sm" && "mb-6",
      gap === "base" && "mb-8",
      className,
    )}
    data-loc={dataLoc}
    id={id}
  >
    {Boolean(label) && (
      <FormItem.Label
        className={labelClassName}
        helpClassName={helpClassName}
        {...props}
      >
        {label}
      </FormItem.Label>
    )}
    {children}
  </div>
);

FormItem.Help = ({ title, body, action, className = "ml-1 mt-1" }) => {
  return title != null || body != null ? (
    <span className={twJoin("inline-block", className)}>
      <Tooltip
        body={body}
        footerAction={action}
        placement="top"
        title={title}
        asChild
      >
        <div className="inline-block">
          <Icon icon={faCircleInfo} size="xs" />
        </div>
      </Tooltip>
    </span>
  ) : null;
};

FormItem.Label = ({
  className,
  children,
  description,
  helpTooltip,
  helpTooltipBody,
  helpAction,
  isRequired,
  dataLoc,
  helpClassName,
}) => {
  return (
    <label
      className={twMerge(
        "mb-2 inline-block font-inter-semibold-13px text-gray-800",
        className,
      )}
      data-loc={dataLoc}
    >
      {children} {isRequired && <span className="text-indigo-500">*</span>}{" "}
      <>
        {(helpTooltip || helpTooltipBody) && (
          <FormItem.Help
            action={helpAction}
            body={helpTooltipBody}
            className={helpClassName}
            title={helpTooltip}
          />
        )}
        {description && (
          <p className="mt-1 font-inter-normal-12px text-gray-500">
            {description}
          </p>
        )}
      </>
    </label>
  );
};
